/**
 * JWT Authentication Service for LabBoxx LIMS
 * Provides secure JWT authentication with automatic token refresh
 * Multi-tenant support with subdomain-based API routing
 * Updated: Fixed token expiry validation for Platform Metrics access
 */

import { subdomainDetector } from '../utils/subdomainDetector';
import type { DeviceTrustInfo, PasskeyStepUp } from '../types';

// Get base API URL but fallback to localhost:8000 for development
function getReliableApiUrl(): string {
  const detectedUrl = subdomainDetector.getApiBaseUrl();
  const hostname = window.location.hostname;
  
  // For development (localhost), preserve subdomain for proper routing
  if (hostname.includes('localhost') || hostname.includes('127.0.0.1')) {
    const protocol = window.location.protocol;
    // If on enterprise subdomain, use enterprise.localhost for backend
    if (hostname.includes('enterprise.localhost')) {
      return `${protocol}//enterprise.localhost:8000`;
    }
    // If on tenant subdomain, use tenant.localhost for backend
    if (hostname.includes('tenant.localhost')) {
      return `${protocol}//tenant.localhost:8000`;
    }
    // Default to plain localhost
    return `${protocol}//localhost:8000`;
  }
  
  // For production, use same origin (nginx proxies to backend)
  if (!detectedUrl) {
    const protocol = window.location.protocol;
    const hostname = window.location.hostname;
    return `${protocol}//${hostname}`;
  }
  return detectedUrl;
}

const API_URL = getReliableApiUrl();
// ALWAYS use enterprise auth endpoints for authentication operations
// This prevents tenant database routing issues during auth
const AUTH_BASE = `${API_URL}/v1/platform/auth`;

export interface AuthTokens {
  access: string;
  refresh?: string;
  expires_in: number;
  token_type: string;
}

export interface User {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  is_active: boolean;
  is_staff: boolean;
  date_joined?: string;
  last_login?: string;
  force_password_change?: boolean;  // For Option D first login password change
}

export interface LoginResponse {
  access_token: string;
  access?: string; // Legacy support
  refresh_token?: string;
  refresh?: string; // Legacy support
  user: User;
  token_type: string;
  expires_in: number;
  refresh_expires_in?: number;
  // Device-trust pairing/passkey info, present only while something needs
  // approving/enrolling (absent in the steady state). passkey_enroll asks the
  // login page to bind a platform passkey after this successful sign-in.
  device_trust?: DeviceTrustInfo;
}

export interface AuthError {
  error: string;
  detail?: string;
}

class JWTAuthService {
  private accessToken: string | null = null;
  private refreshToken: string | null = null;
  private tokenExpiry: Date | null = null;
  private refreshPromise: Promise<AuthTokens> | null = null;
  private lastClearTime: number = 0;

  constructor() {
    this.loadTokensFromStorage();
  }

  /**
   * Load tokens from localStorage
   */
  private loadTokensFromStorage(): void {
    try {
      console.log('🔄 Loading tokens from localStorage...');
      
      const storedAccess = localStorage.getItem('jwt_access_token');
      const storedRefresh = localStorage.getItem('jwt_refresh_token');
      const storedExpiry = localStorage.getItem('jwt_token_expiry');
      
      console.log('🔄 Raw localStorage data:', {
        hasStoredAccess: !!storedAccess,
        hasStoredRefresh: !!storedRefresh,
        hasStoredExpiry: !!storedExpiry,
        accessPreview: storedAccess ? storedAccess.substring(0, 20) + '...' : 'NULL',
        expiryValue: storedExpiry || 'NULL'
      });
      
      this.accessToken = storedAccess;
      this.refreshToken = storedRefresh;
      
      if (storedExpiry) {
        this.tokenExpiry = new Date(storedExpiry);
        console.log('🔄 Parsed token expiry:', {
          expiryDate: this.tokenExpiry.toISOString(),
          isExpired: new Date() >= this.tokenExpiry,
          minutesToExpiry: Math.round((this.tokenExpiry.getTime() - Date.now()) / (1000 * 60))
        });
      }
      
      console.log('🔄 Tokens loaded into memory:', {
        memoryAccess: !!this.accessToken,
        memoryRefresh: !!this.refreshToken,
        memoryExpiry: !!this.tokenExpiry
      });
    } catch (error) {
      console.error('Error loading tokens from storage:', error);
      this.clearTokens();
    }
  }

  /**
   * Save tokens to localStorage
   */
  private saveTokensToStorage(tokens: AuthTokens): void {
    try {
      console.log('💾 Saving tokens to storage:', {
        hasAccess: !!tokens.access,
        hasRefresh: !!tokens.refresh,
        expiresIn: tokens.expires_in
      });
      
      this.accessToken = tokens.access;
      if (tokens.refresh) {
        this.refreshToken = tokens.refresh;
        localStorage.setItem('jwt_refresh_token', tokens.refresh);
      }
      
      // Calculate expiry time (subtract 1 minute for buffer)
      const expiryTime = new Date(Date.now() + (tokens.expires_in - 60) * 1000);
      this.tokenExpiry = expiryTime;
      
      localStorage.setItem('jwt_access_token', tokens.access);
      localStorage.setItem('jwt_token_expiry', expiryTime.toISOString());
      
      console.log('✅ Tokens saved successfully:', {
        accessStored: !!localStorage.getItem('jwt_access_token'),
        refreshStored: !!localStorage.getItem('jwt_refresh_token'),
        inMemoryAccess: !!this.accessToken,
        inMemoryRefresh: !!this.refreshToken
      });
    } catch (error) {
      console.error('Error saving tokens to storage:', error);
    }
  }

  /**
   * Clear all tokens with rate limiting to prevent excessive clearing
   */
  private clearTokens(): void {
    // Prevent clearing tokens too frequently (within 1 second)
    const now = Date.now();
    if (now - this.lastClearTime < 1000) {
      console.warn('🧹 clearTokens() called too frequently, skipping');
      return;
    }
    this.lastClearTime = now;

    // Create stack trace to identify who called clearTokens
    const stack = new Error().stack;
    console.warn('🧹 clearTokens() called! Stack trace:', stack);
    console.warn('🧹 Clearing tokens - before clear:', {
      hasAccessToken: !!this.accessToken,
      hasRefreshToken: !!this.refreshToken,
      accessTokenInStorage: !!localStorage.getItem('jwt_access_token'),
      refreshTokenInStorage: !!localStorage.getItem('jwt_refresh_token'),
      timestamp: new Date().toISOString()
    });
    
    this.accessToken = null;
    this.refreshToken = null;
    this.tokenExpiry = null;
    this.refreshPromise = null;
    
    localStorage.removeItem('jwt_access_token');
    localStorage.removeItem('jwt_refresh_token');
    localStorage.removeItem('jwt_token_expiry');
    localStorage.removeItem('jwt_user');
    
    console.warn('🧹 Tokens cleared successfully!');
  }

  /**
   * Check if access token is expired
   */
  private isTokenExpired(): boolean {
    if (!this.tokenExpiry) return true;
    return new Date() >= this.tokenExpiry;
  }

  /**
   * Check if token is our simple base64 format (not JWT)
   */
  private isSimpleToken(token: string): boolean {
    // Simple tokens don't have JWT format (no dots)
    return Boolean(token && !token.includes('.'));
  }

  /**
   * Login with email and password
   */
  async login(email: string, password: string, totpCode?: string, passkey?: PasskeyStepUp): Promise<LoginResponse> {
  const loginUrl = `${AUTH_BASE}/login/`;
    console.log('🔗 Attempting login to:', loginUrl);
    console.log('📡 Request payload:', { email, password: '***' });

    try {
      const response = await fetch(loginUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include', // Include cookies for refresh token
        body: JSON.stringify({
          email, password,
          ...(totpCode ? { totp_code: totpCode } : {}),
          // GateBOXX passkey (WebAuthn) step-up - a require_passkey=required
          // tenant makes the resubmit carry the navigator.credentials.get()
          // result plus the login host. Inert for every tenant on
          // require_passkey=off (a normal login sends none of these).
          ...(passkey ? {
            passkey_assertion: passkey.assertion,
            rp_id: passkey.rpId,
            origin: passkey.origin,
          } : {}),
        }),
      });

      console.log('📊 Response status:', response.status, response.statusText);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('❌ Login failed response:', errorData);
        const detail = typeof errorData.detail === 'string' ? errorData.detail : errorData.detail?.detail;
        const err = new Error(detail || errorData.error || 'Login failed');
        // Carry the break-glass 2FA flag so the login page can reveal the code field.
        (err as any).require_2fa = !!(errorData.require_2fa || errorData.breakglass);
        // Device-trust enforce refusal: carry the approval code so the login
        // page can show it (the caller reads it to their admin, who approves
        // this browser under Core Settings → Device Trust).
        (err as any).device_not_trusted = errorData.error === 'device_not_trusted';
        (err as any).pairing_code = errorData.pairing_code || '';
        // First-time requesters may be asked who they are (toggleable in the
        // console) so the admin can call back before approving this browser.
        (err as any).collect_contact = !!errorData.collect_contact;
        (err as any).contact_required = !!errorData.contact_required;
        // GateBOXX passkey step-up: a require_passkey=required tenant refuses an
        // enrolled-but-unproven device with a WebAuthn challenge. `reason` is
        // the clean detect signal; passkey_challenge arrives JSON-encoded (the
        // backend json.dumps'd it so DRF's ErrorDetail can't mangle its numeric
        // leaves) - the login page JSON.parses it before use.
        (err as any).reason = errorData.reason || '';
        (err as any).passkey_required = !!errorData.passkey_required;
        (err as any).passkey_challenge = errorData.passkey_challenge ?? null;
        throw err;
      }

      const data: LoginResponse = await response.json();
      console.log('✅ Login response data (full):', {
        hasAccess: !!(data.access_token || data.access),
        hasRefresh: !!(data.refresh_token || data.refresh),
        hasUser: !!data.user,
        tokenType: data.token_type,
        expiresIn: data.expires_in,
        refreshExpiresIn: data.refresh_expires_in
      });
      
      // Extract tokens with backend compatibility (access_token vs access)
      const accessToken = data.access_token || data.access;
      const refreshToken = data.refresh_token || data.refresh;
      
      if (!accessToken) {
        throw new Error('No access token received from server');
      }
      
      // Save tokens
      this.saveTokensToStorage({
        access: accessToken,
        refresh: refreshToken,
        expires_in: data.expires_in,
        token_type: data.token_type,
      });
      
      console.log('🔍 Token extraction and storage:', {
        originalAccessTokenKey: data.access_token ? 'access_token' : (data.access ? 'access' : 'NONE'),
        originalRefreshTokenKey: data.refresh_token ? 'refresh_token' : (data.refresh ? 'refresh' : 'NONE'),
        extractedAccessToken: accessToken ? 'EXTRACTED' : 'NULL',
        extractedRefreshToken: refreshToken ? 'EXTRACTED' : 'NULL',
        expiresIn: data.expires_in
      });

      // Save user data
      localStorage.setItem('jwt_user', JSON.stringify(data.user));

      return data;
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  }

  /**
   * Refresh the access token with better race condition handling
   */
  private async refreshAccessToken(): Promise<AuthTokens> {
    // If there's already a refresh in progress, return that promise
    if (this.refreshPromise) {
      console.log('🔄 Token refresh already in progress, waiting for existing promise...');
      return this.refreshPromise;
    }

    console.log('🔄 Starting new token refresh...');
    this.refreshPromise = this._performTokenRefresh();
    
    try {
      const tokens = await this.refreshPromise;
      console.log('✅ Token refresh completed successfully');
      return tokens;
    } catch (error) {
      console.error('❌ Token refresh failed:', error);
      throw error;
    } finally {
      this.refreshPromise = null;
    }
  }

  /**
   * Perform the actual token refresh
   */
  private async _performTokenRefresh(): Promise<AuthTokens> {
    try {
      // If we have a refresh token in memory, send it. Otherwise rely on httpOnly cookie set by backend.
      const bodyPayload = this.refreshToken ? JSON.stringify({ refresh: this.refreshToken }) : JSON.stringify({});
  const response = await fetch(`${AUTH_BASE}/refresh/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include', // Ensure cookies (refresh_token) included
        body: bodyPayload,
      });

      if (!response.ok) {
        // If refresh fails, clear tokens
        this.clearTokens();
        throw new Error('Token refresh failed');
      }

      const data = await response.json();
      
      // Extract tokens with backend compatibility (access_token vs access)
      const accessToken = data.access_token || data.access;
      const refreshToken = data.refresh_token || data.refresh;
      
      if (!accessToken) {
        throw new Error('No access token received from refresh');
      }
      
      // Save new tokens
      const tokens: AuthTokens = {
        access: accessToken,
        // Backend deliberately strips refresh from body (cookie rotation). Preserve existing if present.
        refresh: refreshToken || this.refreshToken || undefined,
        expires_in: data.expires_in,
        token_type: data.token_type || 'Bearer',
      };

      this.saveTokensToStorage(tokens);
      return tokens;

    } catch (error) {
      console.error('Token refresh error:', error);
      this.clearTokens();
      throw error;
    }
  }

  /**
   * Get valid access token (refresh if needed) with security safeguards
   */
  async getAccessToken(): Promise<string | null> {
    const tokenId = Math.random().toString(36).substr(2, 9);
    
    console.log(`🔑 [${tokenId}] getAccessToken called:`, {
      hasAccessToken: !!this.accessToken,
      tokenExpired: this.isTokenExpired(),
      tokenExpiry: this.tokenExpiry?.toISOString(),
      currentTime: new Date().toISOString()
    });

    // If no token in memory, try loading from storage
    if (!this.accessToken) {
      console.log(`🔄 [${tokenId}] No token in memory, loading from storage...`);
      this.loadTokensFromStorage();
    }

    if (!this.accessToken) {
      console.log(`❌ [${tokenId}] No access token available after storage check`);
      return null;
    }

    // Check if it's a simple token (base64 encoded, not JWT)
    if (this.isSimpleToken(this.accessToken)) {
      console.log(`🎯 [${tokenId}] Simple token detected, checking expiry only`);
      if (!this.isTokenExpired()) {
        console.log(`✅ [${tokenId}] Simple token is valid, returning it`);
        return this.accessToken;
      } else {
        console.log(`❌ [${tokenId}] Simple token expired, clearing tokens`);
        this.clearTokens();
        return null;
      }
    }

    // If token is not expired, return it
    if (!this.isTokenExpired()) {
      console.log(`✅ [${tokenId}] Access token is valid, returning it`);
      return this.accessToken;
    }

    // Security safeguard: For regular API calls, be more lenient about expiry
    // Only force refresh if token is significantly expired
    const minutesExpired = this.tokenExpiry ? Math.round((Date.now() - this.tokenExpiry.getTime()) / (1000 * 60)) : 0;
    const hasRefreshMechanism = !!(this.refreshToken || localStorage.getItem('jwt_refresh_token'));
    
    console.log(`🔄 [${tokenId}] Token expiry analysis:`, {
      minutesExpired: minutesExpired,
      hasRefreshMechanism: hasRefreshMechanism,
      tokenExpiry: this.tokenExpiry?.toISOString()
    });
    
    // If token is not severely expired (less than 2 hours), return it anyway
    // Let the API server decide if it's still valid
    if (minutesExpired < 120) { // Less than 2 hours expired
      console.log(`✅ [${tokenId}] Token recently expired (${minutesExpired} min), returning for API to validate`);
      return this.accessToken;
    }
    
    // Only clear tokens if severely expired (6+ hours) without refresh mechanism
    if (minutesExpired > 360 && !hasRefreshMechanism) {
      console.log(`❌ [${tokenId}] Token severely expired (${Math.round(minutesExpired/60)} hours) without refresh, requiring re-login`);
      this.clearTokens();
      return null;
    }

    // Only attempt refresh if we have a refresh mechanism AND token is significantly expired
    if (!hasRefreshMechanism) {
      console.log(`⚠️ [${tokenId}] No refresh mechanism but token not severely expired, returning existing token`);
      return this.accessToken;
    }

    // Token is expired and we have refresh capability, attempt refresh
    console.log(`🔄 [${tokenId}] Attempting token refresh for expired token...`);

    try {
      const tokens = await this.refreshAccessToken();
      console.log(`✅ [${tokenId}] Token refreshed successfully`);
      return tokens.access;
    } catch (error) {
      console.error(`❌ [${tokenId}] Failed to refresh token:`, error);
      
      // Security safeguard: Only clear tokens if refresh fails multiple times
      const failedRefreshKey = 'jwt_refresh_failed_count';
      const lastFailTime = localStorage.getItem('jwt_last_refresh_fail');
      const now = Date.now();
      
      if (lastFailTime && (now - parseInt(lastFailTime, 10)) < 60000) { // Within 1 minute
        const failCount = parseInt(localStorage.getItem(failedRefreshKey) || '0', 10) + 1;
        localStorage.setItem(failedRefreshKey, failCount.toString());
        localStorage.setItem('jwt_last_refresh_fail', now.toString());
        
        if (failCount >= 3) {
          console.log(`❌ [${tokenId}] Multiple refresh failures, clearing tokens`);
          localStorage.removeItem(failedRefreshKey);
          localStorage.removeItem('jwt_last_refresh_fail');
          this.clearTokens();
        } else {
          console.log(`⚠️ [${tokenId}] Refresh failed (${failCount}/3), keeping tokens for now`);
        }
      } else {
        // Reset fail counter for new failure window
        localStorage.setItem(failedRefreshKey, '1');
        localStorage.setItem('jwt_last_refresh_fail', now.toString());
        console.log(`⚠️ [${tokenId}] First refresh failure in new window, keeping tokens`);
      }
      
      return null;
    }
  }

  /**
   * Public helper to attempt a silent refresh (used by API clients on 401)
   * Returns true if refresh succeeded.
   */
  async attemptRefresh(): Promise<boolean> {
    try {
      const tokens = await this.refreshAccessToken();
      return !!tokens.access;
    } catch (_e) {
      return false;
    }
  }

  /**
   * Force logout & redirect to login (helper for API wrappers) - DISABLED FOR NUCLEAR TESTING
   */
  async forceLogoutRedirect(): Promise<void> {
    console.warn('🚨 forceLogoutRedirect called - NUCLEAR MODE: NO REDIRECT ALLOWED');
    // await this.logout(); // Disabled
    // window.location.href = '/login'; // Disabled
    console.log('🔄 Nuclear mode: Ignoring logout redirect request');
  }

  /**
   * Logout user
   */
  async logout(): Promise<void> {
    try {
      // Call logout endpoint to blacklist refresh token
      if (this.refreshToken) {
  await fetch(`${AUTH_BASE}/logout/`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(this.accessToken && { 'Authorization': `Bearer ${this.accessToken}` }),
          },
          credentials: 'include',
          body: JSON.stringify({
            refresh_token: this.refreshToken,
          }),
        });
      }
    } catch (error) {
      console.error('Logout API error:', error);
    } finally {
      // Always clear tokens locally
      this.clearTokens();
    }
  }

  /**
   * Get current user
   */
  getCurrentUser(): User | null {
    try {
      const userStr = localStorage.getItem('jwt_user');
      if (!userStr || userStr === 'undefined' || userStr === 'null') {
        return null;
      }
      return JSON.parse(userStr);
    } catch (error) {
      console.error('Error getting current user:', error);
      return null;
    }
  }

  /**
   * Check if user is authenticated with security safeguards
   */
  isAuthenticated(): boolean {
    console.log('🔍 JWT isAuthenticated check:', {
      hasAccessToken: !!this.accessToken,
      hasRefreshToken: !!this.refreshToken,
      accessTokenFromStorage: localStorage.getItem('jwt_access_token') ? 'EXISTS' : 'NULL',
      refreshTokenFromStorage: localStorage.getItem('jwt_refresh_token') ? 'EXISTS' : 'NULL',
      userInStorage: localStorage.getItem('jwt_user') ? 'EXISTS' : 'NULL'
    });
    
    // If tokens not in memory, try to reload from storage
    if (!this.accessToken) {
      console.log('🔄 No token in memory, reloading from storage...');
      this.loadTokensFromStorage();
      console.log('🔄 After reload - accessToken:', this.accessToken ? 'EXISTS' : 'NULL');
    }
    
    // Check if token exists
    if (!this.accessToken) {
      console.log('❌ No access token available');
      return false;
    }
    
    // Security safeguard: Be more lenient about token expiry for authentication checks
    // Only reject if token is severely expired AND no refresh mechanism exists
    const hasUserData = !!localStorage.getItem('jwt_user');
    const hasRefreshMechanism = !!(this.refreshToken || localStorage.getItem('jwt_refresh_token'));
    
    console.log('🔍 Authentication assessment:', {
      hasAccessToken: !!this.accessToken,
      hasUserData: hasUserData,
      hasRefreshMechanism: hasRefreshMechanism,
      tokenExpiry: this.tokenExpiry?.toISOString()
    });
    
    // Check if token is severely expired (more than 1 hour) without refresh
    if (this.tokenExpiry && !hasRefreshMechanism) {
      const hoursExpired = (Date.now() - this.tokenExpiry.getTime()) / (1000 * 60 * 60);
      if (hoursExpired > 1) {
        console.log('❌ Token severely expired without refresh mechanism');
        return false;
      }
    }
    
    // If we have an access token, consider authenticated
    // Let API calls handle token refresh when needed
    console.log('✅ Have access token - considering authenticated');
    return true;
  }

  /**
   * Get user profile from API
   */
  async getUserProfile(): Promise<User> {
    const token = await this.getAccessToken();
    if (!token) {
      throw new Error('No access token available');
    }

    // For simple tokens, extract user data from the token itself
    if (this.isSimpleToken(token)) {
      console.log('🎯 Simple token detected, extracting user data from token');
      try {
        const tokenData = JSON.parse(atob(token));
        console.log('🔍 Token data structure:', tokenData);
        
        // Handle different token structures
        let user: User;
        if (tokenData.user) {
          // If user data is nested under 'user' key
          user = tokenData.user;
        } else if (tokenData.user_id || tokenData.email) {
          // If user data is at root level
          user = {
            id: tokenData.user_id || tokenData.id,
            email: tokenData.email,
            first_name: tokenData.first_name || '',
            last_name: tokenData.last_name || '',
            is_active: tokenData.is_active !== false,
            is_staff: tokenData.is_staff === true,
          };
        } else {
          // Fallback: create minimal user from available data
          console.log('⚠️ Token structure not recognized, creating minimal user data');
          user = {
            id: 'unknown',
            email: 'admin@labboxx.com', // Default email
            first_name: 'Admin',
            last_name: 'User',
            is_active: true,
            is_staff: true,
          };
        }
        
        console.log('👤 Extracted user data:', user);
        localStorage.setItem('jwt_user', JSON.stringify(user));
        return user;
      } catch (error) {
        console.error('❌ Failed to parse simple token:', error);
        console.log('🔧 Creating fallback user data for simple token');
        // Create fallback user data instead of throwing error
        const fallbackUser: User = {
          id: 'fallback',
          email: 'admin@labboxx.com',
          first_name: 'Admin',
          last_name: 'User',
          is_active: true,
          is_staff: true,
        };
        localStorage.setItem('jwt_user', JSON.stringify(fallbackUser));
        return fallbackUser;
      }
    }

  const response = await fetch(`${AUTH_BASE}/profile/`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error('Failed to fetch user profile');
    }

    const data = await response.json();
    // Support both { user: {...} } and direct user object
    const user: User = data?.user ?? data;
    // Update stored user data safely
    if (user) {
      localStorage.setItem('jwt_user', JSON.stringify(user));
    }
    return user;
  }

  /**
   * Verify if current token is valid
   */
  async verifyToken(): Promise<boolean> {
    const token = await this.getAccessToken();
    if (!token) return false;

    // For simple tokens, just check if they're not expired (already done in getAccessToken)
    if (this.isSimpleToken(token)) {
      console.log('🎯 Simple token detected, assuming valid (expiry already checked)');
      return true;
    }

    try {
  const response = await fetch(`${AUTH_BASE}/verify/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token }),
      });

      const data = await response.json();
      return data.valid === true;
    } catch (error) {
      console.error('Token verification error:', error);
      return false;
    }
  }
}

// Export singleton instance
export const jwtAuthService = new JWTAuthService();
